"use client";

import { useStore } from "@/state-management/context";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { CiLocationOn } from "react-icons/ci";
import { MdAddHomeWork } from "react-icons/md";
import { FaMoneyBillWave } from "react-icons/fa";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { properties, regions } from "@/data/main-data";
import SectionProperty from "./SectionProperty";

export default function PropertiesSidebar({ slug = null }: any) {
  const locale = useLocale();
  const t = useTranslations("Properties");
  const { filter, setFilter } = useStore();

  const router = useRouter();
  const searchParams = useSearchParams();

  const typeFromUrl = searchParams.get("type");
  const tab = searchParams.get("tab");

  const [open, setOpen] = useState({
    property: true,
    location: false,
    price: true,
  });

  const types = ["buy", "rent"];

  const currentPath = usePathname();

  // check url include developer to show only buy option
  const showBuyOnly = currentPath?.includes("developer");

  /* =========================
      Helpers
  ========================= */

  function updateUrl(updatedFilter: any) {
    const query = new URLSearchParams({
      type: updatedFilter?.propertyType.join(","),
      location: updatedFilter?.location.join(","),
      min_price: updatedFilter?.price?.min || "",
      max_price: updatedFilter?.price?.max || "",
      tab: tab || "",
    });

    if (!showBuyOnly) {
      router.push(`/properties?${query.toString()}`);
      return;
    }
    router.push(`/developer/${slug}/units?${query.toString()}`);
  }

  /* =========================
      Property Type
  ========================= */

  const togglePropertyType = (item: any) => {
    const updated = filter.propertyType.includes(item.value)
      ? filter.propertyType.filter((e: string) => e !== item.value)
      : [...filter.propertyType, item.value];

    const updatedFilter = {
      ...filter,
      propertyType: updated,
    };

    setFilter(updatedFilter);
    updateUrl(updatedFilter);
  };

  /* =========================
      Location
  ========================= */

  const toggleLocation = (region: any) => {
    const updated = filter.location.includes(region.value)
      ? filter.location.filter((e: string) => e !== region.value)
      : [...filter.location, region.value];

    const updatedFilter = {
      ...filter,
      location: updated,
    };

    setFilter(updatedFilter);
    updateUrl(updatedFilter);
  };

  /* =========================
      Price
  ========================= */

  const setPriceRange = (key: "min" | "max", value: string) => {
    const updatedFilter = {
      ...filter,
      price: {
        ...filter.price,
        [key]: value,
      },
    };

    setFilter(updatedFilter);
    updateUrl(updatedFilter);
  };

  /* =========================
      Init from URL
  ========================= */

  useEffect(() => {
    if (typeFromUrl) {
      setFilter({
        ...filter,
        propertyType: typeFromUrl.split(","),
      });
    }
  }, []);

  /* =========================
      UI
  ========================= */

  return (
    <div
      className="
        w-full max-w-95 bg-white rounded-3xl border border-gray-100 shadow-[0_10px_40px_rgba(0,0,0,0.06)] p-5 space-y-6 sm:sticky sm:top-24"
    >
      <h2 className="text-lg font-extrabold text-gray-800">
        {locale === "ar" ? "تصفية العقارات" : "Filter Properties"}
      </h2>

      {/* Buy / Rent */}
      <div className="flex bg-gray-100 p-1 rounded-2xl">
        {types.map((type) => (
          <button
            key={type}
            onClick={() => setFilter({ ...filter, type })}
            className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer ${
              filter.type === type
                ? "bg-main text-white shadow"
                : "text-gray-600 hover:text-main"
            }`}
          >
            {t(type)}
          </button>
        ))}
      </div>

      {/* Property Type */}
      <SectionProperty
        icon={<MdAddHomeWork />}
        title={t("propertyType")}
        isOpen={open.property}
        onToggle={() => setOpen({ ...open, property: !open.property })}
      >
        <div className="grid grid-cols-2 gap-3">
          {properties.map((item) => {
            const selected = filter.propertyType.includes(item.value);
            return (
              <button
                key={item.value}
                onClick={() => togglePropertyType(item)}
                className={`px-3 py-2 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                  selected
                    ? "bg-main text-white border-main shadow"
                    : "bg-gray-50 border-gray-300 hover:border-main hover:text-main"
                }`}
              >
                {locale === "ar" ? item.label_ar : item.label_en}
              </button>
            );
          })}
        </div>
      </SectionProperty>

      {/* Location */}
      <SectionProperty
        icon={<CiLocationOn size={20} />}
        title={t("location")}
        isOpen={open.location}
        onToggle={() => setOpen({ ...open, location: !open.location })}
      >
        <div className="grid grid-cols-2 gap-2 max-h-50 overflow-y-auto pr-1">
          {regions.map((region) => {
            const selected = filter.location.includes(region.value);
            return (
              <button
                key={region.value}
                onClick={() => toggleLocation(region)}
                className={`px-3 py-2 rounded-xl border text-xs font-medium transition ${
                  selected
                    ? "bg-main text-white border-main"
                    : "bg-gray-50 border-gray-200 hover:border-main"
                }`}
              >
                {locale === "ar" ? region.label_ar : region.label_en}
              </button>
            );
          })}
        </div>
      </SectionProperty>

      {/* Price */}
      <SectionProperty
        icon={<FaMoneyBillWave />}
        title={t("price")}
        isOpen={open.price}
        onToggle={() => setOpen({ ...open, price: !open.price })}
      >
        <div className="grid grid-cols-2 gap-3">
          <input
            type="number"
            placeholder={locale === "ar" ? "أقل سعر" : "Min Price"}
            min={0}
            onChange={(e) => setPriceRange("min", e.target.value)}
            className="
              px-4 py-2 rounded-xl border border-gray-300
              focus:border-main focus:ring-2 focus:ring-main/20
              outline-none text-sm
            "
          />
          <input
            type="number"
            placeholder={locale === "ar" ? "أعلى سعر" : "Max Price"}
            min={0}
            onChange={(e) => setPriceRange("max", e.target.value)}
            className="
              px-4 py-2 rounded-xl border border-gray-300
              focus:border-main focus:ring-2 focus:ring-main/20
              outline-none text-sm
            "
          />
        </div>
      </SectionProperty>

      {/* Reset */}
      <button
        onClick={() =>
          setFilter({
            type: "buy",
            propertyType: [],
            location: [],
            price: { min: "", max: "" },
          })
        }
        className="w-full py-2 rounded-xl border border-red-200 text-red-500 text-sm font-bold  hover:bg-red-50 transition cursor-pointer"
      >
        {locale === "ar" ? "إعادة تعيين الفلاتر" : "Reset Filters"}
      </button>
    </div>
  );
}
